import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, Clock, Pencil } from "lucide-react";

const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export default function SchedulesPanel({ tenantId = "", playlists = null, compact = false }) {
  const { user } = useAuth();
  const isSuper = user?.role === "SUPER_ADMIN";
  const [tenants, setTenants] = useState([]);
  const [tid, setTid] = useState(tenantId);
  const [pls, setPls] = useState(playlists || []);
  const [schedules, setSchedules] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "", playlist_id: "", weekdays: [],
    start_time: "", end_time: "", start_date: "", end_date: "", priority: 0,
  });

  useEffect(() => {
    if (tenantId) setTid(tenantId);
  }, [tenantId]);

  useEffect(() => {
    if (isSuper && !tenantId) {
      api.get("/tenants").then((r) => {
        setTenants(r.data);
        if (r.data.length) setTid((c) => c || r.data[0].id);
      }).catch(() => {});
    }
  }, [isSuper, tenantId]);

  const load = useCallback(() => {
    api.get("/schedules").then((r) => {
      const all = r.data;
      setSchedules(isSuper && tid ? all.filter((s) => s.tenant_id === tid) : all);
    }).catch(() => {});
    if (!playlists) {
      api.get("/playlists").then((r) => setPls(r.data)).catch(() => {});
    }
  }, [isSuper, tid, playlists]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const visiblePlaylists = pls.filter((p) => !isSuper || !tid || p.tenant_id === tid);

  function toggleDay(d) {
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(d) ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d].sort((a, b) => a - b),
    }));
  }

  async function save(e) {
    e.preventDefault();
    if (!form.playlist_id) return;
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        playlist_id: form.playlist_id,
        weekdays: form.weekdays,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        priority: Number(form.priority) || 0,
      };
      if (editingId) {
        await api.patch(`/schedules/${editingId}`, payload);
        toast.success("Zeitplan aktualisiert — Geräte synchronisieren automatisch");
      } else {
        if (isSuper) payload.tenant_id = tid;
        await api.post("/schedules", payload);
        toast.success("Zeitplan angelegt — Geräte synchronisieren automatisch");
      }
      cancelEdit();
      load();
    } catch (e2) {
      toast.error(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(s) {
    setEditingId(s.id);
    setForm({
      name: s.name || "", playlist_id: s.playlist_id, weekdays: s.weekdays || [],
      start_time: s.start_time || "", end_time: s.end_time || "",
      start_date: s.start_date || "", end_date: s.end_date || "", priority: s.priority ?? 0,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm({ name: "", playlist_id: "", weekdays: [], start_time: "", end_time: "", start_date: "", end_date: "", priority: 0 });
  }

  async function toggleActive(s) {
    try {
      await api.patch(`/schedules/${s.id}`, { active: !s.active });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function remove(s) {
    try {
      await api.delete(`/schedules/${s.id}`);
      toast.success("Zeitplan gelöscht");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  function describe(s) {
    const days = s.weekdays?.length ? s.weekdays.map((d) => DAYS[d]).join(" ") : "täglich";
    const time = s.start_time || s.end_time ? `${s.start_time || "00:00"}–${s.end_time || "24:00"} Uhr` : "ganztägig";
    const dates = s.start_date || s.end_date ? ` · ${s.start_date || "…"} bis ${s.end_date || "…"}` : "";
    return `${days} · ${time}${dates}`;
  }

  return (
    <div className={compact ? "panel p-5" : ""} data-testid="schedules-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock size={15} className="text-cyan-400" />
          <span className="font-display font-bold text-slate-100">Zeitpläne</span>
        </div>
        {isSuper && !compact && !tenantId && (
          <select value={tid} onChange={(e) => setTid(e.target.value)} className="input-dark !w-48 !py-1.5" data-testid="schedule-tenant-select">
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}
      </div>
      <p className="text-[11px] text-slate-300 leading-relaxed mb-4">
        Zeitpläne steuern, wann welche Playlist läuft. Greift ein Zeitplan, gewinnt der mit der höchsten
        Priorität — sonst läuft die Standard-Playlist des Geräts.
      </p>

      <form onSubmit={save} className="space-y-3 mb-5" data-testid="schedule-form">
        <div className="grid grid-cols-2 gap-2">
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="input-dark"
            placeholder="Name, z. B. Frühstück"
            data-testid="schedule-name-input"
          />
          <select
            value={form.playlist_id}
            onChange={(e) => setForm((f) => ({ ...f, playlist_id: e.target.value }))}
            className="input-dark"
            required
            data-testid="schedule-playlist-select"
          >
            <option value="">— Playlist —</option>
            {visiblePlaylists.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-1" data-testid="schedule-weekdays">
          {DAYS.map((d, i) => (
            <button
              type="button"
              key={d}
              onClick={() => toggleDay(i)}
              className={`flex-1 py-1.5 rounded-sm border font-mono text-[10px] transition-colors duration-150 ${
                form.weekdays.includes(i)
                  ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/10"
                  : "border-border text-slate-300 hover:text-slate-300"
              }`}
              data-testid={`schedule-day-${i}`}
            >
              {d}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mono-label block mb-1">Von</label>
            <input type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} className="input-dark" data-testid="schedule-start-time" />
          </div>
          <div>
            <label className="mono-label block mb-1">Bis</label>
            <input type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} className="input-dark" data-testid="schedule-end-time" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="mono-label block mb-1">Startdatum</label>
            <input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className="input-dark" data-testid="schedule-start-date" />
          </div>
          <div>
            <label className="mono-label block mb-1">Enddatum</label>
            <input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} className="input-dark" data-testid="schedule-end-date" />
          </div>
          <div>
            <label className="mono-label block mb-1">Priorität</label>
            <input type="number" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className="input-dark" data-testid="schedule-priority-input" />
          </div>
        </div>
        <div className="flex gap-2">
          {editingId && (
            <button type="button" onClick={cancelEdit} className="btn-ghost text-xs" data-testid="schedule-cancel-edit">
              Abbrechen
            </button>
          )}
          <button type="submit" disabled={busy || !form.playlist_id} className="btn-primary flex-1" data-testid="schedule-save-button">
            <Plus size={14} /> {busy ? "Speichere…" : editingId ? "Änderungen speichern" : "Zeitplan anlegen"}
          </button>
        </div>
      </form>

      <div className="space-y-2" data-testid="schedule-list">
        {schedules.length ? (
          schedules.map((s) => (
            <div
              key={s.id}
              className={`flex items-center gap-3 border rounded-sm p-3 transition-colors duration-150 ${
                s.active ? "border-border" : "border-border/50 opacity-50"
              }`}
              data-testid={`schedule-row-${s.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs text-slate-200 font-medium truncate">
                  {s.name || s.playlist_name}
                  {s.name ? <span className="text-slate-300 font-normal"> → {s.playlist_name}</span> : null}
                </div>
                <div className="font-mono text-[10px] text-slate-300 mt-0.5">
                  {describe(s)} · Prio {s.priority}
                </div>
              </div>
              <button onClick={() => startEdit(s)} className="p-1.5 text-slate-400 hover:text-cyan-300 transition-colors" title="Zeitplan bearbeiten" data-testid={`schedule-edit-${s.id}`}>
                <Pencil size={13} />
              </button>
              <button
                onClick={() => toggleActive(s)}
                className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${
                  s.active
                    ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                    : "text-slate-300 border-border"
                }`}
                data-testid={`schedule-toggle-${s.id}`}
              >
                {s.active ? "Aktiv" : "Aus"}
              </button>
              <button onClick={() => remove(s)} className="p-1.5 text-slate-400 hover:text-red-400 transition-colors" data-testid={`schedule-delete-${s.id}`}>
                <Trash2 size={13} />
              </button>
            </div>
          ))
        ) : (
          <div className="font-mono text-[11px] text-slate-400 text-center py-4" data-testid="schedule-empty">
            Keine Zeitpläne — Standard-Playlist läuft durchgehend
          </div>
        )}
      </div>
    </div>
  );
}
